import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { MailOpenIcon } from "@heroicons/react/solid";
import Image from "next/image";
import Link from "next/link";
import { Layout } from "../components/layout";
import { FormInput as Input } from "../components/form/input";
import gql from "graphql-tag";
import { useMutation } from "@apollo/react-hooks";
import { emailRegex } from "../utils/regexPatterns";
import { RenderStep, Stepper } from "../components/stepper";
import { ProgressIndicatorVertical } from "../components/stepper/progressIndicator";
import { FormCheckbox } from "../components/form/checkbox";
import { SubscriptionPricingPlans } from "../components/settings/subscriptionPricingPlans";
import { PalmaLogo } from "../components/logo";

const CREATE_ACCOUNT = gql`
  mutation (
    $companyName: String!
    $authName: String!
    $permitNumber: String!
    $firstName: String!
    $lastName: String!
    $emailAddress: String!
    $termsOfService: Boolean!
  ) {
    createAccount(
      memberFirstName: $firstName
      memberLastName: $lastName
      orgAuthName: $authName
      orgDisplayName: $companyName
      orgResidentPermitNumber: $permitNumber
      tosAccepted: $termsOfService
      userEmail: $emailAddress
    ) {
      accountID
    }
  }
`;

const validationSchema = {
  companyName: {
    minLength: {
      value: 3,
      message: "Company Name must be between 3 and 100 characters",
    },
    maxLength: {
      value: 100,
      message: "Company Name must be between 3 and 100 characters",
    },
  },
  authName: {
    pattern: {
      value: /^[a-z0-9]+[a-z0-9_-]*$/,
      message:
        "Authorization Name must only contain lowercase characters, '-', and '_', and start with a letter or number",
    },
    minLength: {
      value: 3,
      message: "Authorization Name must be between 3 and 50 characters",
    },
    maxLength: {
      value: 50,
      message: "Authorization Name must be between 3 and 50 characters",
    },
  },
  permitNumber: {
    pattern: {
      value: /^[8]+[0-9]*$/,
      message:
        "Resident Permit Number must start with an 8 and can only contain numbers",
    },
    minLength: {
      value: 14,
      message: "Resident Permit Number must be 14 characters long",
    },
    maxLength: {
      value: 14,
      message: "Resident Permit Number must be 14 characters long",
    },
  },
  firstName: {
    maxLength: {
      value: 50,
      message: "First Name must be between 1 and 50 characters",
    },
  },
  lastName: {
    maxLength: {
      value: 50,
      message: "Last Name must be between 1 and 50 characters",
    },
  },
  emailAddress: {
    pattern: {
      value: emailRegex,
      message: "Email address must be a valid email",
    },
  },
};

type RegistrationPageProps = {};
const RegistrationPage: React.FC<RegistrationPageProps> = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [steps, setSteps] = useState<RenderStep[]>([]);
  return (
    <Layout headTitle="Registration">
      <div className="flex flex-col w-full lg:flex-row">
        <div className="bg-gray-100 flex w-full p-6 lg:flex-col lg:w-5/12 lg:justify-start lg:p-10 lg:max-w-md lg:pt-10 space-y-16">
          <PalmaLogo size="sm" />
          <div className="mb-16 hidden lg:flex lg:flex-col">
            <div className="text-2xl font-bold tracking-tight">
              Sign up with Palma
            </div>
            <div className="text-lg text-gray-600">
              Follow the steps to get started
            </div>
          </div>
          <ProgressIndicatorVertical
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            steps={steps}
          />
        </div>
        <RegistrationForm {...{ activeStep, setActiveStep, setSteps }} />
      </div>
    </Layout>
  );
};

type RegistrationFormProps = {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setSteps: React.Dispatch<React.SetStateAction<RenderStep[]>>;
};

const RegistrationForm = ({
  activeStep,
  setActiveStep,
  setSteps,
}: RegistrationFormProps) => {
  const { register, handleSubmit, setValue, formState, reset } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
  });
  const { errors, isValid } = formState;

  const [selectedPricingPlan, setSelectedPricingPlan] = useState(0);

  const [createAccount, { loading }] = useMutation(CREATE_ACCOUNT, {
    onCompleted: () => {
      console.log("Successfully created account!");
      setActiveStep(4);
      reset();
    },
    onError: (error) => {
      throw Error(error.message);
    },
  });

  const handleAuthName = (e) => {
    var authName = e.target.value
      .replace(/ /g, "-")
      .replace(".", "")
      .toLowerCase();
    setValue("authName", authName);
  };

  useEffect(() => {
    setSteps(steps);
    reset();
  }, []);

  const steps = [
    {
      title: "Company Details",
      subtitle: "Add your company's basic information",
      render: <CompanyDetailsStep {...{ register, errors, handleAuthName }} />,
    },
    {
      title: "Personal Details",
      subtitle: "Fill in your personal information",
      render: <PersonalDetailsStep {...{ register, errors }} />,
    },
    {
      title: "Select a plan",
      render: (
        <PricingStep
          {...{ register, errors, selectedPricingPlan, setSelectedPricingPlan }}
        />
      ),
    },
  ];

  const onFormSubmit = async (data) => {
    // console.log(data);
    if (activeStep == 3) {
      await createAccount({
        variables: {
          ...data,
        },
      });
    }
  };

  return (
    <div className="flex flex-col w-full">
      <form
        className="mx-auto w-full sm:max-w-2xl mt-24"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        {steps.length > 0 && (
          <Stepper
            {...{ steps, activeStep, setActiveStep, isValid }}
            centered
            progressIndicator="small"
            finalButton={{
              text: "Create Account",
              onClick: handleSubmit(onFormSubmit),
              loading,
              loadingText: "Creating",
            }}
            EndScreen={RegistrationSuccessScreen}
          />
        )}
      </form>
    </div>
  );
};

const CompanyDetailsStep = ({ register, errors, handleAuthName }) => (
  <div className="flex flex-col mx-auto space-y-6  sm:w-5/6">
    <Input
      label="Company Name"
      fieldName="companyName"
      placeholder="Wayne Enterprises"
      onChangeMethod={handleAuthName}
      required
      {...{ register, errors, validationSchema }}
    />
    <Input
      label="Company URL"
      fieldName="authName"
      placeholder="wayne-enterprises"
      alert="The Company URL cannot be changed in the future."
      addOnText="palma.hn/company/"
      tooltip="The Company URL can be used by the employees to log in directly to the company's dashboard"
      onChangeMethod={handleAuthName}
      required
      {...{ register, errors, validationSchema }}
    />
    <Input
      label="Resident Permit Number"
      fieldName="permitNumber"
      placeholder="83154498809922"
      tooltip="The Resident Permit Number is the company's official identification number in Próspera"
      required
      {...{ register, errors, validationSchema }}
    />
  </div>
);

const PersonalDetailsStep = ({ register, errors }) => (
  <div className="flex flex-col mx-auto space-y-6 sm:w-5/6">
    <Input
      label="First Name"
      fieldName="firstName"
      placeholder="Bruce"
      required
      {...{ register, errors, validationSchema }}
    />
    <Input
      label="Last Name"
      fieldName="lastName"
      placeholder="Wayne"
      required
      {...{ register, errors, validationSchema }}
    />
    <Input
      label="Email Address"
      fieldName="emailAddress"
      placeholder="bruce.wayne@example.com"
      required
      {...{ register, errors, validationSchema }}
    />
  </div>
);

const PricingStep = ({
  register,
  errors,
  selectedPricingPlan,
  setSelectedPricingPlan,
}) => (
  <div className="flex flex-col mx-auto space-y-6 items-center">
    <SubscriptionPricingPlans
      {...{ selectedPricingPlan, setSelectedPricingPlan }}
    />
    <FormCheckbox
      label={
        <>
          I agree to the{" "}
          <Link href={"/terms"}>
            <a className="text-green-600 hover:text-green-700">
              Terms of Service
            </a>
          </Link>{" "}
          and{" "}
          <Link href={"/terms"}>
            <a className="text-green-600 hover:text-green-700">
              Privacy Policy
            </a>
          </Link>
        </>
      }
      fieldName="termsOfService"
      {...{ register, errors }}
    />
  </div>
);

const RegistrationSuccessScreen = () => (
  <div className="flex flex-col items-center mt-24">
    <div className="bg-green-100 mb-8 rounded-full">
      <MailOpenIcon width={150} height={150} className="text-green-500 m-8" />
    </div>
    <div className="flex flex-col items-center">
      <div className="uppercase text-primary-500 font-black">
        You're all set!
      </div>
      <div className="text-4xl font-extrabold tracking-tight">
        Check your inbox
      </div>
      <div className="text-xl text-gray-500 w-2/3 text-center mt-2">
        The account was successfully created! Please check your email for the
        next steps.
      </div>
    </div>
  </div>
);

export default RegistrationPage;
